/**
 * A headset's buttons and lights as WebHID shows them (1.13.0, Teampace 3141): the standard
 * telephony controls (HID usage table, Telephony page 0x0B and LED page 0x08) that Jabra, Yealink,
 * Poly (Plantronics) and EPOS headsets all have, the same ones Microsoft Teams uses.
 *
 * Pure: the device's report layout in, where each button and light is out; a report's bytes read
 * into values, and lights written into a report's bytes. No vendor's own protocol.
 */

/** A usage: page << 16 | id. */
export type Usage = number;
export const usage = (page: number, id: number): Usage => ((page & 0xffff) << 16) | (id & 0xffff);

export const TELEPHONY = 0x0b;
export const LED = 0x08;

export const U = {
  /** Answer / hang up: 1 off the hook (on a call), 0 on it. */
  hookSwitch: usage(TELEPHONY, 0x20),
  /** Hold, or swap calls. */
  flash: usage(TELEPHONY, 0x21),
  redial: usage(TELEPHONY, 0x24),
  /** End the call (some headsets: decline). */
  drop: usage(TELEPHONY, 0x26),
  phoneMute: usage(TELEPHONY, 0x2f),
  /** Jabra's "reject" (a double press while ringing). */
  lineBusyTone: usage(TELEPHONY, 0x97),
  /** A base station's own ringer (output). */
  ringer: usage(TELEPHONY, 0x9e),
  ledMute: usage(LED, 0x09),
  ledOffHook: usage(LED, 0x17),
  ledRing: usage(LED, 0x18),
  ledHold: usage(LED, 0x20),
  ledMicrophone: usage(LED, 0x21)
} as const;

/** The buttons Stitch reads, and the lights it sets. */
export const BUTTONS: readonly Usage[] = [U.hookSwitch, U.flash, U.drop, U.phoneMute, U.lineBusyTone, U.redial];
export const LIGHTS: readonly Usage[] = [U.ledOffHook, U.ledRing, U.ledMute, U.ledHold, U.ledMicrophone, U.ringer];

/** The parts of WebHID's HIDReportItem this needs. */
export interface ReportItem {
  usages?: number[];
  usageMinimum?: number;
  usageMaximum?: number;
  isRange?: boolean;
  isAbsolute?: boolean;
  isConstant?: boolean;
  reportSize?: number;
  reportCount?: number;
  logicalMinimum?: number;
  logicalMaximum?: number;
}
export interface ReportInfo {
  reportId?: number;
  items?: ReportItem[];
}
export interface CollectionInfo {
  usagePage?: number;
  usage?: number;
  inputReports?: ReportInfo[];
  outputReports?: ReportInfo[];
  featureReports?: ReportInfo[];
  children?: CollectionInfo[];
}

/** One control in a report: where its bits are. */
export interface Field {
  usage: Usage;
  /** Bits from the start of the report's data (after the report id). */
  offset: number;
  size: number;
  /** False: the control says "pressed", not a state (a toggle; Jabra's mute, some hook switches). */
  absolute: boolean;
  signed: boolean;
}

export interface Report {
  id: number;
  /** Bits in the whole report (for the bytes to send). */
  bits: number;
  fields: Field[];
}

export interface Layout {
  inputs: Map<number, Report>;
  outputs: Map<number, Report>;
  /** The device has telephony controls at all. */
  telephony: boolean;
}

function fieldsOf(r: ReportInfo, wanted: ReadonlySet<Usage>): Report {
  const fields: Field[] = [];
  let offset = 0;
  for (const item of r.items ?? []) {
    const size = Math.max(0, item.reportSize ?? 0);
    const count = Math.max(0, item.reportCount ?? 0);
    let usages: number[] = item.usages ?? [];
    if (item.isRange && item.usageMinimum !== undefined && item.usageMaximum !== undefined && item.usageMaximum >= item.usageMinimum && item.usageMaximum - item.usageMinimum < 256) {
      usages = Array.from({ length: item.usageMaximum - item.usageMinimum + 1 }, (_, i) => item.usageMinimum! + i);
    }
    for (let i = 0; i < count; i++) {
      // The HID rule: more controls than usages, and the last usage repeats.
      const u = usages.length ? usages[Math.min(i, usages.length - 1)]! : undefined;
      if (u !== undefined && !item.isConstant && wanted.has(u >>> 0) && !fields.some((f) => f.usage === u >>> 0)) {
        fields.push({ usage: u >>> 0, offset: offset + i * size, size, absolute: item.isAbsolute !== false, signed: (item.logicalMinimum ?? 0) < 0 });
      }
    }
    offset += size * count;
  }
  return { id: r.reportId ?? 0, bits: offset, fields };
}

function walk(c: CollectionInfo, out: CollectionInfo[]): void {
  out.push(c);
  for (const k of c.children ?? []) walk(k, out);
}

/** Where a device's buttons and lights are, from its collections (only the telephony ones). */
export function layoutOf(collections: readonly CollectionInfo[]): Layout {
  const inputs = new Map<number, Report>();
  const outputs = new Map<number, Report>();
  const buttons = new Set(BUTTONS);
  const lights = new Set(LIGHTS);
  let telephony = false;
  for (const top of collections) {
    if (top.usagePage !== TELEPHONY) continue;
    telephony = true;
    const all: CollectionInfo[] = [];
    walk(top, all);
    for (const c of all) {
      for (const r of c.inputReports ?? []) {
        const rep = fieldsOf(r, buttons);
        if (rep.fields.length && !inputs.has(rep.id)) inputs.set(rep.id, rep);
      }
      for (const r of c.outputReports ?? []) {
        const rep = fieldsOf(r, lights);
        if (rep.fields.length && !outputs.has(rep.id)) outputs.set(rep.id, rep);
      }
    }
  }
  return { inputs, outputs, telephony };
}

function readBits(data: DataView, offset: number, size: number): number {
  let v = 0;
  for (let i = 0; i < size && i < 32; i++) {
    const bit = offset + i;
    const byte = bit >> 3;
    if (byte >= data.byteLength) break;
    if ((data.getUint8(byte) >> (bit & 7)) & 1) v |= 1 << i;
  }
  return v >>> 0;
}

/** A report's buttons, as values (1 pressed or on, 0 not). */
export function readInput(layout: Layout, reportId: number, data: DataView): Map<Usage, number> {
  const out = new Map<Usage, number>();
  const rep = layout.inputs.get(reportId);
  if (!rep) return out;
  for (const f of rep.fields) {
    let v = readBits(data, f.offset, f.size);
    if (f.signed && f.size > 0 && f.size < 32 && v & (1 << (f.size - 1))) v -= 1 << f.size;
    out.set(f.usage, v);
  }
  return out;
}

/** The reports to send for these lights (each report that has any of them, every other bit 0). */
export function writeOutput(layout: Layout, lights: ReadonlyMap<Usage, number>): { id: number; data: Uint8Array }[] {
  const out: { id: number; data: Uint8Array }[] = [];
  for (const rep of layout.outputs.values()) {
    if (!rep.fields.some((f) => lights.has(f.usage))) continue;
    const data = new Uint8Array(Math.ceil(rep.bits / 8));
    for (const f of rep.fields) {
      const v = lights.get(f.usage) ?? 0;
      for (let i = 0; i < f.size && i < 32; i++) {
        if ((v >> i) & 1) {
          const bit = f.offset + i;
          data[bit >> 3]! |= 1 << (bit & 7);
        }
      }
    }
    out.push({ id: rep.id, data });
  }
  return out;
}

/** Whether a button's control reports a state (true) or a press (false): hook switches differ by model. */
export function isAbsolute(layout: Layout, u: Usage): boolean {
  for (const rep of layout.inputs.values()) {
    const f = rep.fields.find((x) => x.usage === u);
    if (f) return f.absolute;
  }
  return true;
}

/** The device has this button (or light). */
export const hasButton = (layout: Layout, u: Usage) => [...layout.inputs.values()].some((r) => r.fields.some((f) => f.usage === u));
export const hasLight = (layout: Layout, u: Usage) => [...layout.outputs.values()].some((r) => r.fields.some((f) => f.usage === u));

/** Headset makers by USB vendor id (what Stitch calls them; any other telephony headset works the same way). */
export const BRANDS: Record<number, string> = {
  0x0b0e: 'Jabra',
  0x047f: 'Poly',
  0x6993: 'Yealink',
  0x1395: 'EPOS',
  0x03f0: 'HP Poly'
};
export const brandOf = (vendorId: number): string | null => BRANDS[vendorId] ?? null;
